import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CellContext } from '@tanstack/react-table';

import { Input } from '@/components/ui/input';
import {
  cn,
  dateTimeLocalToISOString,
  formatDateForDisplay,
  formatDateForInput,
} from '@/lib/utils';
import { isEditableShipVisitColumnId } from '@/routes/(app)/(scenarios)/(create)/components/editable-cell.utils';
import {
  CellValue,
  ShipVisit,
} from '@/routes/(app)/(scenarios)/(create)/types';

export const EditableCell: FC<CellContext<ShipVisit, CellValue>> = ({
  getValue,
  row,
  column,
  table,
}) => {
  const initialValue = getValue();
  const [value, setValue] = useState<CellValue>(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);
  // Set by Escape so the blur that follows discards the edit.
  const isCancelling = useRef(false);

  const { selectedCell, setSelectedCell, updateData } =
    table.options.meta ?? {};

  const isLocked = !!table.options.meta?.isRowLocked?.(row.original);
  const isReadOnly = !!column.columnDef.meta?.readOnly || isLocked;
  const inputType = column.columnDef.meta?.inputType ?? 'text';
  const isNumberColumn = inputType === 'number';
  const isDateColumn = inputType === 'datetime-local';

  const rowId = row.original.id;
  const isSelected =
    !!selectedCell &&
    selectedCell.rowId === rowId &&
    selectedCell.columnId === column.id;

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (isSelected && inputRef.current) {
      inputRef.current.focus();

      if (inputRef.current.type === 'text') {
        const length = inputRef.current.value.length;
        inputRef.current.setSelectionRange(length, length);
      }
    }
  }, [isSelected]);

  const handleActivate = () => {
    setSelectedCell?.({ rowId, columnId: column.id });
  };

  const handleBlur = () => {
    const isCancelled = isCancelling.current;
    isCancelling.current = false;
    if (
      !isCancelled &&
      value !== initialValue &&
      isEditableShipVisitColumnId(column.id)
    ) {
      updateData?.(rowId, column.id, value);
    }
    // A rejected edit must not leave the typed value behind.
    setValue(initialValue);
    setSelectedCell?.(null);
  };

  // Enter saves and Escape cancels; both leave the cell through blur.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' && event.key !== 'Escape') return;
    event.preventDefault();
    isCancelling.current = event.key === 'Escape';
    inputRef.current?.blur();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;

    if (isDateColumn) {
      setValue(rawValue ? dateTimeLocalToISOString(rawValue) : '');
      return;
    }

    if (isNumberColumn) {
      setValue(rawValue === '' ? null : Number(rawValue));
      return;
    }

    setValue(rawValue);
  };

  const displayValue = useMemo(() => {
    if (isDateColumn) {
      return formatDateForDisplay(initialValue);
    }
    if (isNumberColumn) {
      return initialValue == null ? '' : Number(initialValue);
    }
    return String(initialValue);
  }, [isDateColumn, isNumberColumn, initialValue]);

  if (isReadOnly) {
    return (
      <div
        className={cn(
          'p-2 rounded h-full min-h-[2rem] flex items-center',
          isLocked && 'text-muted-foreground opacity-60'
        )}>
        <span className='truncate'>{displayValue}</span>
      </div>
    );
  }

  if (!isSelected) {
    return (
      <div
        role='button'
        tabIndex={0}
        onClick={handleActivate}
        className='cursor-pointer hover:bg-muted/50 p-2 rounded h-full min-h-[2rem] flex items-center'>
        <span className='truncate'>{displayValue}</span>
      </div>
    );
  }

  return (
    <Input
      ref={inputRef}
      className='w-full h-8'
      type={inputType}
      value={isDateColumn ? formatDateForInput(value) : String(value ?? '')}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  );
};
